import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { AdminPage, Field, Notice, OnOffBadge, SkeletonList, SkeletonTable, Switch } from '@/admin/AdminShell'
import { Icon } from '@/components/Icon'
import { StateView } from '@/card/record'
import { ApiError } from '@/lib/api'
import {
  fetchMessengerEvents,
  fetchMessengerSettings,
  registerTelegramWebhook,
  updateMessengerSettings,
  type MessengerChannel,
  type MessengerSettings,
} from './api'

/**
 * Мессенджеры в администрировании (тикет #125): бот Telegram и номер WhatsApp Business, журнал
 * вебхуков.
 *
 * Токены и секреты только записываются: сервер отвечает, заданы ли они, но не какие. Пустое поле
 * оставляет сохранённое значение. Адрес вебхука показывается целиком — без него ни одно сообщение
 * клиента до CRM не дойдёт. У Telegram адрес регистрирует кнопка («setWebhook» с сохранённым
 * секретом): секрет на экран не выводится, и копировать его руками было бы нечем.
 *
 * Право — `system.integration`, как у телефонии: токен бота и токен Meta — учётные данные.
 */
export function MessengerSettingsPage() {
  const { t, user } = useApp()
  const mayRead = can(user, 'system.integration', 'read')
  const settings = useQuery({
    queryKey: ['admin-messengers'],
    queryFn: ({ signal }) => fetchMessengerSettings(signal),
    enabled: mayRead,
  })
  return (
    <AdminPage titleKey="messenger.adm.title" leadKey="messenger.adm.lead" forbidden={!mayRead}
               readOnly={!can(user, 'system.integration', 'update')}>
      {!mayRead ? (
        <StateView boxed kind="forbidden" title={t('messenger.adm.forbidden')} />
      ) : settings.error ? (
        <StateView kind="error" compact title={t('messenger.adm.error')} />
      ) : !settings.data ? (
        <SkeletonList rows={6} />
      ) : (
        <div className="stack" style={{ gap: 20 }}>
          <ChannelPanel channel="telegram" current={settings.data.telegram} />
          <ChannelPanel channel="whatsapp" current={settings.data.whatsapp} />
          <EventsPanel />
        </div>
      )}
    </AdminPage>
  )
}

/** Secrets of a channel the screen may send: typed value, or nothing (keep the stored one). */
type Secrets = { token: string; secret: string; appSecret: string; verifyToken: string }

const EMPTY: Secrets = { token: '', secret: '', appSecret: '', verifyToken: '' }

function ChannelPanel({ channel, current }: { channel: MessengerChannel; current: MessengerSettings }) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const mayEdit = can(user, 'system.integration', 'update')
  const [draft, setDraft] = useState<MessengerSettings>(current)
  const [secrets, setSecrets] = useState<Secrets>(EMPTY)
  const [notice, setNotice] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [registered, setRegistered] = useState<string | null>(null)

  useEffect(() => {
    setDraft(current)
  }, [current])

  const fail = (failure: unknown) => setNotice(failure instanceof ApiError ? failure.message : t('app.error'))
  const save = useMutation({
    mutationFn: () =>
      updateMessengerSettings(channel, {
        enabled: draft.enabled,
        endpoint: draft.endpoint?.trim() || null,
        token: secrets.token === '' ? undefined : secrets.token,
        secret: secrets.secret === '' ? undefined : secrets.secret,
        appSecret: secrets.appSecret === '' ? undefined : secrets.appSecret,
        verifyToken: secrets.verifyToken === '' ? undefined : secrets.verifyToken,
        phoneNumberId: channel === 'whatsapp' ? draft.phoneNumberId?.trim() ?? null : undefined,
      }),
    onSuccess: (value) => {
      setNotice(null)
      setSecrets(EMPTY)
      setSaved(true)
      queryClient.setQueryData(['admin-messengers'], (old: Record<MessengerChannel, MessengerSettings> | undefined) =>
        old ? { ...old, [channel]: value } : old)
    },
    onError: (failure) => {
      setSaved(false)
      fail(failure)
    },
  })
  const webhook = `${window.location.origin}${draft.webhookPath}`
  const register = useMutation({
    mutationFn: () => registerTelegramWebhook(webhook),
    onSuccess: (value) => {
      setNotice(null)
      setRegistered(value.url)
    },
    onError: fail,
  })

  const set = (patch: Partial<MessengerSettings>) => {
    setSaved(false)
    setDraft({ ...draft, ...patch })
  }
  const secret = (key: keyof Secrets, label: string, isSet: boolean) => (
    <Field label={label} htmlFor={`${channel}-${key}`}
           help={isSet ? t('messenger.adm.secret.set') : t('messenger.adm.secret.unset')}>
      <input id={`${channel}-${key}`} className="input" type="password" autoComplete="new-password" disabled={!mayEdit}
             value={secrets[key]} onChange={(e) => { setSaved(false); setSecrets({ ...secrets, [key]: e.target.value }) }} />
    </Field>
  )
  const title = t(`messenger.channel.${channel}`)

  return (
    <section className="panel stack" style={{ gap: 12 }} aria-label={title}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h2">{title}</span>
        <OnOffBadge on={current.enabled} />
        {current.enabled && !current.usable && <span className="badge b-danger">{t('messenger.adm.incomplete')}</span>}
      </div>
      {current.problem && (
        <div className="alert alert-warning" role="note">
          <Icon name="alert" />
          <span>{current.problem}</span>
        </div>
      )}
      <div className="alert alert-info" role="note">
        <Icon name="comment" />
        <span>
          {t(`messenger.adm.webhook.${channel}`)} <code className="mono">{webhook}</code>
        </span>
      </div>
      <form className="stack" style={{ gap: 12 }} aria-label={title} onSubmit={(event) => {
        event.preventDefault()
        if (mayEdit) save.mutate()
      }}>
        <Switch checked={draft.enabled} disabled={!mayEdit} label={t(`messenger.adm.enabled.${channel}`)}
                onChange={(value) => set({ enabled: value })} />
        <div className="form-grid">
          <Field label={t('messenger.adm.endpoint')} htmlFor={`${channel}-endpoint`} help={t(`messenger.adm.endpoint.${channel}`)}>
            <input id={`${channel}-endpoint`} className="input mono" disabled={!mayEdit} value={draft.endpoint ?? ''}
                   onChange={(e) => set({ endpoint: e.target.value })} />
          </Field>
          {channel === 'whatsapp' && (
            <Field label={t('messenger.adm.phoneNumberId')} htmlFor="whatsapp-phone" help={t('messenger.adm.phoneNumberId.help')}>
              <input id="whatsapp-phone" className="input mono" disabled={!mayEdit} value={draft.phoneNumberId ?? ''}
                     onChange={(e) => set({ phoneNumberId: e.target.value })} />
            </Field>
          )}
          {secret('token', t(`messenger.adm.token.${channel}`), current.tokenSet)}
          {channel === 'telegram'
            ? secret('secret', t('messenger.adm.secret'), current.secretSet)
            : (
              <>
                {secret('appSecret', t('messenger.adm.appSecret'), current.appSecretSet)}
                {secret('verifyToken', t('messenger.adm.verifyToken'), current.verifyTokenSet)}
              </>
            )}
        </div>
        <Notice text={notice} />
        {saved && <span className="t-sm muted" role="status">{t('messenger.adm.saved')}</span>}
        {registered && <span className="t-sm muted" role="status">{t('messenger.adm.registered', { url: registered })}</span>}
        {mayEdit && (
          <div className="row" style={{ gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {save.isPending ? t('adm.saving') : t('admin.save')}
            </button>
            {channel === 'telegram' && (
              <button type="button" className="btn btn-secondary" disabled={!current.tokenSet || register.isPending}
                      onClick={() => register.mutate()}>
                {t('messenger.adm.register')}
              </button>
            )}
          </div>
        )}
      </form>
    </section>
  )
}

function EventsPanel() {
  const { t, locale } = useApp()
  const events = useQuery({ queryKey: ['admin-messenger-events'], queryFn: ({ signal }) => fetchMessengerEvents(signal) })
  return (
    <section className="panel stack" style={{ gap: 12 }} aria-label={t('messenger.adm.events')}>
      <span className="h2">{t('messenger.adm.events')}</span>
      {events.isLoading ? (
        <SkeletonTable columns={4} rows={4} />
      ) : events.error ? (
        <StateView kind="error" compact title={t('messenger.adm.error')} />
      ) : (events.data ?? []).length === 0 ? (
        <p className="muted">{t('messenger.adm.events.empty')}</p>
      ) : (
        <table className="card-table" aria-label={t('messenger.adm.events')}>
          <thead>
            <tr>
              <th>{t('messenger.adm.events.at')}</th>
              <th>{t('messenger.adm.events.channel')}</th>
              <th>{t('messenger.adm.events.status')}</th>
              <th>{t('messenger.adm.events.detail')}</th>
            </tr>
          </thead>
          <tbody>
            {(events.data ?? []).map((event) => (
              <tr key={event.id}>
                <td>{new Date(event.receivedAt).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')}</td>
                <td>{t(`messenger.channel.${event.channel}`)}</td>
                <td>
                  <span className={`badge${event.status === 'rejected' || event.status === 'failed' ? ' b-danger' : ''}`}>
                    {t(`messenger.event.${event.status}`)}
                  </span>
                </td>
                <td className="t-sm">{event.detail ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
